import React from 'react';
import { GraduationCap, IdentificationCard, Copy } from 'phosphor-react';
import { useFetch } from '_api';
import { useAlertActions } from '_actions';
import { formatVND, formatDate } from '_helpers';
import { PageHeader, Bezel, Table, Tag, MotionReveal, LoadingScreen, EmptyState } from '_ui';

function methodLabel(m) {
    return m === 'cash' ? 'Tiền mặt' : 'Chuyển khoản';
}

export function LopCuaToi() {
    const { data, loading } = useFetch('/api/study/me');

    if (loading) return <LoadingScreen />;
    const enrollments = data?.enrollments || [];

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Học viên"
                title="Lớp của tôi"
                description="Tiến độ học, học phí và lịch sử đóng tiền của bạn."
            />

            {enrollments.length === 0 ? (
                <Bezel>
                    <EmptyState
                        icon={GraduationCap}
                        title="Bạn chưa được xếp lớp"
                        description="Khi được xếp vào một lớp học, thông tin lớp, học phí và mã đóng học phí của bạn sẽ hiện ở đây."
                    />
                </Bezel>
            ) : (
                <div className="space-y-8">
                    {enrollments.map((en, i) => (
                        <MotionReveal key={en.enrollmentId} delay={i * 0.05}>
                            <EnrollmentCard enrollment={en} />
                        </MotionReveal>
                    ))}
                </div>
            )}
        </div>
    );
}

function EnrollmentCard({ enrollment: en }) {
    const alert = useAlertActions();
    const settled = en.remaining <= 0;
    const pct = en.totalSessions > 0 ? Math.min(100, Math.round((en.attendanceCount / en.totalSessions) * 100)) : 0;

    function copyCode() {
        if (navigator.clipboard) navigator.clipboard.writeText(en.tuitionCode).then(() => alert.success('Đã sao chép mã'));
    }

    const paymentColumns = [
        { key: 'paidAt', header: 'Ngày', render: p => <span className="tnum text-espresso-soft">{formatDate(p.paidAt, true)}</span> },
        { key: 'method', header: 'Hình thức', render: p => <Tag tone="neutral">{methodLabel(p.method)}</Tag> },
        { key: 'note', header: 'Ghi chú', render: p => p.note || <span className="text-espresso-soft/50">—</span> },
        { key: 'amount', header: 'Số tiền', align: 'right', render: p => <span className="tnum font-medium text-sage-deep">{formatVND(p.amount)}</span> },
    ];

    return (
        <Bezel className="space-y-7 p-6 sm:p-8">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="font-serif text-3xl text-espresso">{en.className}</h2>
                    <p className="mt-1 text-sm text-espresso-soft/80">Đã học {en.attendanceCount} / {en.totalSessions} buổi</p>
                </div>
                {!en.classActive && <Tag tone="neutral">Lớp đã kết thúc</Tag>}
            </div>

            {/* Progress bar */}
            <div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-espresso/[0.08]">
                    <div className="h-full rounded-full bg-sage-deep transition-all duration-500 ease-fluid" style={{ width: `${pct}%` }} />
                </div>
                <p className="mt-1.5 text-xs text-espresso-soft/70">{pct}% chương trình học</p>
            </div>

            {/* Tuition */}
            <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl bg-cream-200/50 p-4 ring-1 ring-espresso/10">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-espresso-soft/70">Học phí</p>
                    <p className="mt-2 tnum text-xl font-medium text-espresso">{formatVND(en.tuitionPrice)}</p>
                </div>
                <div className="rounded-2xl bg-cream-200/50 p-4 ring-1 ring-espresso/10">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-espresso-soft/70">Đã đóng</p>
                    <p className="mt-2 tnum text-xl font-medium text-espresso">{formatVND(en.paidTotal)}</p>
                </div>
                <div className="rounded-2xl bg-cream-200/50 p-4 ring-1 ring-espresso/10">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-espresso-soft/70">Còn lại</p>
                    <p className={`mt-2 tnum text-xl font-semibold ${settled ? 'text-sage-deep' : 'text-danger'}`}>
                        {settled ? 'Đã đóng đủ học phí' : formatVND(en.remaining)}
                    </p>
                </div>
            </div>

            {/* Tuition code */}
            <div className="flex flex-col items-start gap-3 rounded-2xl bg-brass/10 p-5 ring-1 ring-brass/20 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brass/15 text-brass-deep">
                        <IdentificationCard size={22} weight="light" />
                    </span>
                    <div>
                        <p className="text-sm text-espresso-soft">Khi chuyển khoản học phí, ghi mã này vào nội dung chuyển khoản:</p>
                        <p className="tnum mt-1 font-mono text-lg font-semibold tracking-wide text-espresso">{en.tuitionCode}</p>
                    </div>
                </div>
                <button onClick={copyCode} className="inline-flex items-center gap-2 self-start rounded-full bg-cream-50 px-4 py-2 text-sm font-medium text-espresso ring-1 ring-espresso/10 transition-colors hover:bg-cream-200/60 sm:self-auto">
                    <Copy size={15} weight="light" /> Sao chép mã
                </button>
            </div>

            {/* Payment history */}
            <div>
                <h3 className="mb-3 font-serif text-lg text-espresso">Lịch sử đóng học phí</h3>
                <div className="overflow-hidden rounded-squircle-inner ring-1 ring-espresso/[0.06]">
                    <Table columns={paymentColumns} rows={en.payments || []} empty="Chưa có lần đóng học phí nào." />
                </div>
            </div>

            {/* Attendance dates */}
            <div>
                <h3 className="mb-3 font-serif text-lg text-espresso">Các buổi đã điểm danh</h3>
                {(!en.attendanceDates || en.attendanceDates.length === 0) ? (
                    <p className="text-sm text-espresso-soft/70">Chưa có buổi điểm danh nào.</p>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {en.attendanceDates.map(d => (
                            <Tag key={d} tone="sage">{formatDate(d)}</Tag>
                        ))}
                    </div>
                )}
            </div>
        </Bezel>
    );
}
